import React, { useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Phone } from 'lucide-react';

/**
 * Shared marketing primitives for the outbound marketing pages.
 *
 * Follows AlfredVoice_Style_Guide.md — the concierge brand:
 *  - White page (`paper`), warm cards (`warm`), deep ink text.
 *  - Brass is a seasoning, never a paint: eyebrows, prices, links, one CTA.
 *  - ONE dark moment per page — a single ink card carrying the line you most
 *    want remembered. Hence `DarkStatement`, used once per page, and never a
 *    full-bleed dark section.
 *  - The wave is the only flourish. No other iconography.
 *  - Poppins only; weight carries hierarchy (300 lead / 400 body / 600 label /
 *    700 heading). Cards 22px (`rounded-brand`), buttons full pill.
 */

export const DEMO_PHONE_DISPLAY = '(346) 258-0006';
export const DEMO_PHONE_TEL = 'tel:3462580006';

// ---- The wave — logo mark and "Alfred is listening" only -------------------

export const Wave = ({ live = false, className = '' }) => (
  <span className={`av-wave ${live ? 'av-wave--live' : ''} ${className}`} aria-hidden="true">
    <span /><span /><span /><span /><span /><span /><span />
  </span>
);

// ---- Page behavior (smooth anchors, fade-in, scroll reset) -----------------

export const useMarketingPage = () => {
  useEffect(() => {
    window.scrollTo(0, 0);

    // In-page anchors. The previous version called preventDefault() and then
    // relied solely on smooth scrolling — so anywhere `behavior: 'smooth'` is a
    // no-op, the click did nothing at all, having already cancelled the native
    // jump. Never cancel the default without a scroll we can verify happened.
    const handleAnchorClick = (e) => {
      if (e.defaultPrevented || e.button !== 0 || e.metaKey || e.ctrlKey || e.shiftKey) return;
      const href = e.target.closest('a')?.getAttribute('href');
      if (!href || href === '#' || !href.startsWith('#')) return;

      const target = document.querySelector(href);
      if (!target) return; // unknown target — leave it to the browser

      e.preventDefault();
      const prefersReduced = window.matchMedia?.('(prefers-reduced-motion: reduce)').matches;
      const before = window.scrollY;
      target.scrollIntoView({ behavior: prefersReduced ? 'auto' : 'smooth', block: 'start' });

      // If nothing moved shortly after, smooth scrolling isn't being honored —
      // jump instantly so the click always does something.
      setTimeout(() => {
        if (Math.abs(window.scrollY - before) < 2) target.scrollIntoView({ block: 'start' });
      }, 300);
    };
    document.addEventListener('click', handleAnchorClick);

    const reveal = (el) => {
      el.style.opacity = '1';
      el.style.transform = 'translateY(0)';
    };

    // No IntersectionObserver (or a browser that never delivers the callback)
    // must not leave sections permanently invisible — the content is the point,
    // the animation is not. Everything starts visible and is only hidden once
    // we know we can reveal it, with a timeout as a second safety net.
    if (typeof IntersectionObserver === 'undefined') {
      return () => document.removeEventListener('click', handleAnchorClick);
    }

    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            reveal(entry.target);
            observer.unobserve(entry.target);
          }
        });
      },
      { threshold: 0.1, rootMargin: '0px 0px -50px 0px' }
    );

    let failsafe;
    const timer = setTimeout(() => {
      const cards = document.querySelectorAll('.fade-in');
      cards.forEach((card) => {
        card.style.opacity = '0';
        card.style.transform = 'translateY(30px)';
        card.style.transition = 'opacity 0.6s ease, transform 0.6s ease';
        observer.observe(card);
      });
      // If the observer hasn't revealed a card within 2s, show it anyway.
      failsafe = setTimeout(() => cards.forEach((card) => {
        if (card.style.opacity === '0') reveal(card);
      }), 2000);
    }, 100);

    return () => {
      clearTimeout(timer);
      clearTimeout(failsafe);
      document.removeEventListener('click', handleAnchorClick);
      observer.disconnect();
    };
  }, []);
};

// ---- Layout ---------------------------------------------------------------

// Sections alternate paper / warm, separated by hairlines. Never dark.
// scroll-mt-24 keeps an anchored section clear of the sticky header (~57px)
// when it is scrolled to.
export const Section = ({ id, tone = 'paper', border = true, className = '', children }) => (
  <section
    id={id}
    className={`py-20 ${id ? 'scroll-mt-24' : ''} ${tone === 'warm' ? 'bg-warm' : 'bg-paper'} ${border ? 'border-b border-hair' : ''} ${className}`}
  >
    {children}
  </section>
);

export const SectionHeading = ({ eyebrow, title, subtitle, align = 'center' }) => (
  <div className={`mb-12 ${align === 'center' ? 'text-center' : ''}`}>
    {eyebrow && <span className="av-eyebrow mb-3">{eyebrow}</span>}
    <h2 className="text-3xl md:text-4xl font-bold tracking-tight mb-4">{title}</h2>
    {subtitle && (
      <p className={`text-lg text-slateink font-light max-w-2xl ${align === 'center' ? 'mx-auto' : ''}`}>
        {subtitle}
      </p>
    )}
  </div>
);

// ---- Hero -----------------------------------------------------------------

// phoneDisplay/phoneTel default to the 346 demo line, so every existing page is
// byte-identical. /drivers passes the 832 recruiting line instead — a driver ringing
// about work must not land on the sales demo.
export const Hero = ({
  eyebrow, title, children, secondaryHref, secondaryLabel, footnote, stats,
  phoneDisplay = DEMO_PHONE_DISPLAY, phoneTel = DEMO_PHONE_TEL
}) => (
  <section className="relative overflow-hidden border-b border-hair">
    <div
      className="pointer-events-none absolute inset-0"
      style={{
        background:
          'radial-gradient(700px 380px at 82% 10%, rgba(168,127,69,.10), transparent 60%), radial-gradient(620px 460px at 10% 95%, rgba(14,27,38,.05), transparent 60%)'
      }}
    />
    <div className="relative max-w-4xl mx-auto px-6 py-24 text-center">
      <div className="flex justify-center mb-6"><Wave live /></div>
      {eyebrow && <span className="av-eyebrow mb-4">{eyebrow}</span>}
      <h1 className="text-5xl md:text-6xl font-bold mb-6 leading-[1.05] tracking-tight">{title}</h1>
      {children && (
        <p className="text-lg md:text-xl mb-10 text-slateink font-light max-w-2xl mx-auto">{children}</p>
      )}

      <div className="flex flex-col sm:flex-row gap-3 justify-center">
        {/* Style guide: the phone CTA is the highest-ranking action on the page. */}
        <a href={phoneTel} className="av-btn-brass px-8 py-4 text-sm">
          <Phone className="w-4 h-4" /> Talk to Alfred — {phoneDisplay}
        </a>
        <a href={secondaryHref} className="av-btn-ghost px-8 py-4 text-sm">
          {secondaryLabel}
        </a>
      </div>

      {footnote && (
        <div className="mt-4 text-sm text-slateink flex items-center justify-center gap-2">
          <span className="inline-block w-2 h-2 rounded-full bg-brass" /> {footnote}
        </div>
      )}

      {stats?.length > 0 && (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-8 mt-16 max-w-3xl mx-auto border-t border-hair pt-12">
          {stats.map((stat) => (
            <div key={stat.label} className="text-center">
              <div className="text-4xl font-bold mb-1 text-brass">{stat.value}</div>
              <div className="text-sm text-slateink">{stat.label}</div>
            </div>
          ))}
        </div>
      )}
    </div>
  </section>
);

// ---- The one dark moment --------------------------------------------------

/**
 * A single ink card per page. Two on one screen kills the effect, so each page
 * uses this exactly once, for the line it most wants remembered.
 */
export const DarkStatement = ({ eyebrow, title, children, items, closer, tone = 'paper' }) => (
  <Section tone={tone}>
    <div className="max-w-4xl mx-auto px-6">
      <div className="bg-ink text-white rounded-brand p-10 md:p-14">
        {eyebrow && <span className="av-eyebrow mb-4">{eyebrow}</span>}
        <h2 className="text-3xl md:text-4xl font-bold tracking-tight mb-6">{title}</h2>
        {children && <div className="text-lg text-mist font-light leading-relaxed space-y-5">{children}</div>}

        {items?.length > 0 && (
          <ul className="mt-2 space-y-4">
            {items.map((item) => (
              <li key={item.label} className="flex items-start gap-4 border-b border-white/10 pb-4 last:border-0">
                <span className="flex-shrink-0 mt-1.5 w-1.5 h-1.5 rounded-full bg-brass" />
                <p className="text-mist font-light leading-relaxed">
                  <strong className="text-white font-semibold">{item.label}</strong> {item.detail}
                </p>
              </li>
            ))}
          </ul>
        )}

        {closer && <p className="mt-8 text-mist/80 font-light">{closer}</p>}
      </div>
    </div>
  </Section>
);

// ---- Reality / what Alfred does ------------------------------------------

export const ProblemTable = ({ eyebrow = 'Sound familiar?', title, rows, tone = 'paper' }) => (
  <Section tone={tone}>
    <div className="max-w-5xl mx-auto px-6">
      <SectionHeading eyebrow={eyebrow} title={title} />
      <div className="space-y-4">
        {rows.map((row) => (
          <div
            key={row.problem}
            className={`fade-in ${tone === 'warm' ? 'bg-paper' : 'bg-warm'} border border-hair rounded-brand p-6 md:flex md:items-center md:gap-6`}
          >
            <div className="md:w-1/2 mb-4 md:mb-0">
              <p className="font-semibold">{row.problem}</p>
            </div>
            <div className="flex items-start gap-3 md:w-1/2 md:border-l md:border-hair md:pl-6">
              <span className="flex-shrink-0 mt-1.5 w-1.5 h-1.5 rounded-full bg-brass" />
              <p className="text-slateink font-light">{row.solution}</p>
            </div>
          </div>
        ))}
      </div>
    </div>
  </Section>
);

// ---- Cards ----------------------------------------------------------------

export const CardGrid = ({ items, cols = 3, tone = 'paper' }) => (
  <div className={`grid md:grid-cols-2 ${cols === 3 ? 'lg:grid-cols-3' : ''} gap-6`}>
    {items.map(({ title, description }) => (
      <div
        key={title}
        className={`fade-in ${tone === 'warm' ? 'bg-paper' : 'bg-warm'} border border-hair rounded-brand p-6`}
      >
        <h3 className="text-lg font-bold mb-2">{title}</h3>
        <p className="text-slateink text-sm font-light leading-relaxed">{description}</p>
      </div>
    ))}
  </div>
);

// ---- Steps ---------------------------------------------------------------

export const Steps = ({ eyebrow = 'Live in days, not months', title = 'How it works', steps, tone = 'warm' }) => (
  <Section tone={tone}>
    <div className="max-w-6xl mx-auto px-6">
      <SectionHeading eyebrow={eyebrow} title={title} />
      <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
        {steps.map((step, idx) => (
          <div key={step.title} className="fade-in text-center">
            <div className="text-3xl font-bold text-brass mb-4">{String(idx + 1).padStart(2, '0')}</div>
            <h3 className="text-xl font-bold mb-3">{step.title}</h3>
            <p className="text-slateink font-light">{step.description}</p>
          </div>
        ))}
      </div>
    </div>
  </Section>
);

// ---- Tables --------------------------------------------------------------

/**
 * Money table. By default the last row is the punchline and reads in brass —
 * pass highlightLast={false} for tables that just list options (dispositions),
 * where the final row carries no special weight.
 */
export const MathTable = ({ rows, columns, tone = 'warm', highlightLast = true }) => (
  <div className="overflow-x-auto">
    <table className={`w-full border border-hair text-left ${tone === 'warm' ? 'bg-paper' : 'bg-warm'}`}>
      {columns && (
        <thead>
          <tr>
            <th className="p-4 border-b border-hair" />
            {columns.map((col) => (
              <th key={col} className="p-4 border-b border-hair text-center font-semibold">{col}</th>
            ))}
          </tr>
        </thead>
      )}
      <tbody>
        {rows.map((row, idx) => {
          const isLast = highlightLast && idx === rows.length - 1;
          const [label, ...values] = row;
          return (
            <tr key={label} className={isLast ? '' : 'border-b border-hair'}>
              <td className={`p-4 ${isLast ? 'font-bold' : 'font-semibold'}`}>{label}</td>
              {values.map((v, i) => (
                <td
                  key={i}
                  className={`p-4 ${values.length > 1 ? 'text-center' : 'text-right'} ${
                    isLast ? 'font-bold text-brass' : 'text-slateink'
                  }`}
                >
                  {v}
                </td>
              ))}
            </tr>
          );
        })}
      </tbody>
    </table>
  </div>
);

// ---- FAQ -----------------------------------------------------------------

export const FAQ = ({ eyebrow = 'Straight answers', title = 'Questions, answered', items, tone = 'paper' }) => (
  <Section tone={tone}>
    <div className="max-w-3xl mx-auto px-6">
      <SectionHeading eyebrow={eyebrow} title={title} />
      <div className="divide-y divide-hair border-t border-b border-hair">
        {items.map((item) => (
          <details key={item.q} className="group py-5">
            <summary className="flex cursor-pointer items-center justify-between font-semibold text-lg list-none">
              {item.q}
              <span className="ml-4 text-2xl leading-none text-brass group-open:rotate-45 transition-transform">+</span>
            </summary>
            <p className="mt-3 text-slateink font-light leading-relaxed">{item.a}</p>
          </details>
        ))}
      </div>
    </div>
  </Section>
);

// ---- Final CTA -----------------------------------------------------------

export const FinalCTA = ({
  eyebrow = 'Talk to Alfred', heading, body,
  phoneDisplay = DEMO_PHONE_DISPLAY, phoneTel = DEMO_PHONE_TEL, secondaryTo = '/contact', secondaryLabel = 'Book a discovery call'
}) => (
  <section className="py-20 bg-warm">
    <div className="max-w-3xl mx-auto px-6 text-center">
      <span className="av-eyebrow mb-4">{eyebrow}</span>
      <h2 className="text-3xl md:text-5xl font-bold tracking-tight mb-6">{heading}</h2>
      {body && <p className="text-lg mb-10 text-slateink font-light">{body}</p>}
      <div className="flex flex-col sm:flex-row gap-3 justify-center">
        <a href={phoneTel} className="av-btn-brass px-8 py-4 text-sm">
          <Phone className="w-4 h-4" /> Talk to Alfred — {phoneDisplay}
        </a>
        <Link to={secondaryTo} className="av-btn-ghost px-8 py-4 text-sm">
          {secondaryLabel}
        </Link>
      </div>
    </div>
  </section>
);
